import React, { useRef, useCallback } from 'react'
import classnames from 'classnames/bind'

import Image from 'components/shared/Image'
import { Title, Text } from 'components/typo'
import Link from 'components/shared/Link'

import useAnimateInView from 'hooks/useAnimateInView'
import useIconTitleTextAnimation from 'hooks/useIconTitleTextAnimation'
import useCardMouseAnimations from 'hooks/useCardMouseAnimations'
import { ReactComponent as Arrow } from './arrow.svg'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const CardContainer = ({ link, ...props }) => {
  return link ? <Link {...link} {...props} /> : <div {...props} />
}

const CardsSectionCard = ({ theme, title, icon, text, link, index }) => {
  const componentRef = useRef()
  const elementRef = useRef()
  const backgroundRef = useRef()
  const titleRef = useRef()
  const textRef = useRef()
  const iconRef = useRef()

  const tl = useIconTitleTextAnimation(
    {
      componentRef,
      elementRef,
      titleRef,
      textRef,
      iconRef,
    },
    { index }
  )

  const [inViewRef] = useAnimateInView(
    {
      triggerOnce: true,
      threshold: 0.25,
      ref: componentRef,
      animateIn: () => tl.restart(true),
    },
    []
  )

  const setRef = useCallback((node) => {
    elementRef.current = node
    inViewRef(node)
  }, [])

  const [_, mouseEvents] = useCardMouseAnimations({
    cardRef: elementRef,
    backgroundRef,
  })
  const componentProps = link ? { ...mouseEvents } : {}

  return (
    <li
      className={cx(css.CardsSectionCard, css.withLink)}
      ref={setRef}
      {...componentProps}
    >
      <div className={css.cardLink}>
        <CardContainer className={css.cardLayout} link={link}>
          <div className={css.background} ref={backgroundRef} />
          <div className={cx(css.contentWrapper, { withIcon: icon })}>
            {icon && (
              <div className={css.iconContainer}>
                <span className={css.iconWrapper} ref={iconRef}>
                  <Image className={css.icon} {...icon} />
                </span>
              </div>
            )}
            <div className={css.titleSection}>
              {title && (
                <Title forwardRef={titleRef} className={css.title} size="small">
                  <span>{title}</span>
                  {link && (
                    <span className={css.arrow}>
                      &#65279;
                      <Arrow className={cx(css.arrowIcon, theme)} />
                    </span>
                  )}
                </Title>
              )}
            </div>
            {text && (
              <div className={css.text} ref={textRef}>
                <Text size="medium">{text}</Text>
              </div>
            )}
          </div>
        </CardContainer>
      </div>
    </li>
  )
}

export default CardsSectionCard
